import { store } from '../store';

/**
 * Get current user details from Redux store
 * Replaces the old Cognito getUserDetails() function
 *
 * @returns {Object} User details from Redux Auth state
 */
export const getUserDetails = () => {
  const state = store.getState();
  const auth = state.Auth;

  if (!auth || !auth.userId) {
    return null;
  }

  return {
    userId: auth.userId,
    id: auth.id,
    email: auth.email,
    firstName: auth.firstName,
    lastName: auth.lastName,
    customerId: auth.customerId,
    companyName: auth.companyName,
    systemRole: auth.systemRole,
    systemroles: auth.systemroles,
    clientStatus: auth.clientStatus,
    projectStatus: auth.projectStatus,
    dashboardConfig: auth.dashboardConfig,
    teamRoles: auth.teamRoles,
    theme: auth.theme,
    permissions: auth.permissions,
    bulkUpload: auth.bulkUpload,
    selectedcountries: auth.selectedcountries,
    flatFee: auth.flatFee,
    flatFeeCountryId: auth.flatFeeCountryId,
    userstatus: auth.userstatus,
    portals: auth.portals,
    integrations: auth.integrations,
    phoneNumbers: auth.phoneNumbers,
    aliasName: auth.aliasName,
    features: auth.features,
  };
};

/**
 * Get user info in array format (replaces decrypt('GetUserInfo'))
 * Returns user data in the same format as old localStorage pattern
 *
 * @returns {Array|null} Array with single user object, or null if not authenticated
 */
export const GetUserInfo = () => {
  const auth = store.getState().Auth;

  if (!auth || !auth.userId) {
    return null;
  }

  // Return in array format to match old localStorage pattern: GetUserInfo()[0]
  return [
    {
      userId: auth.userId,
      id: auth.id, //|| auth.customerId,
      email: auth.email,
      firstName: auth.firstName,
      lastName: auth.lastName,
      customerId: auth.customerId,
      companyName: auth.companyName,
      systemRole: auth.systemRole,
      systemroles: auth.systemroles || auth.systemRole,
      clientStatus: auth.clientStatus,
      projectStatus: auth.projectStatus,
      dashboardConfig: auth.dashboardConfig,
      teamRoles: auth.teamRoles,
      theme: auth.theme,
      permissions: auth.permissions,
      bulkUpload: auth.bulkUpload || 25,
      selectedcountries: auth.selectedcountries,
      flatFee: auth.flatFee,
      flatFeeCountryId: auth.flatFeeCountryId,
      userstatus: auth.userstatus,
      portals: auth.portals,
      integrations: auth.integrations,
      features: auth.features,
    },
  ];
};

/**
 * Get user ID (replaces decrypt('GetUserId'))
 *
 * @returns {string|null} User ID or customer ID
 */
export const GetUserId = () => {
  const auth = store.getState().Auth;
  return auth?.customerId || auth?.id || null;
};

/**
 * Get user attributes (replaces localStorage.getItem('getAttributes'))
 * Returns in the same format as old localStorage pattern
 *
 * @returns {Array|null} Array with single user attributes object
 */
export const getAttributes = () => {
  const auth = store.getState().Auth;

  if (!auth || !auth.userId) {
    return null;
  }

  // Return in array format to match old pattern: getAttributes()[0]
  return [
    {
      userId: auth.userId,
      id: auth.id,
      customerId: auth.customerId,
      email: auth.email,
      firstName: auth.firstName,
      lastName: auth.lastName,
      systemRole: auth.systemRole,
      companyName: auth.companyName,
      flatFee: auth.flatFee,
      flatFeeCountryId: auth.flatFeeCountryId,
      userstatus: auth.userstatus,
      portals: auth.portals,
      integrations: auth.integrations,
    },
  ];
};

/**
 * Check if a specific integration is active for the current customer
 * @param {string} integrationType - e.g. "OpenAI", "Ring Central"
 * @returns {boolean}
 */
export const isIntegrationActive = (integrationType) => {
  const userInfo = GetUserInfo();
  const rawIntegrations = userInfo?.[0]?.integrations;
  if (!rawIntegrations) return false;
  let parsed;
  try {
    parsed = typeof rawIntegrations === "string" ? JSON.parse(rawIntegrations) : rawIntegrations;
  } catch {
    return false;
  }
  if (!Array.isArray(parsed)) return false;
  return parsed.some(
    (i) => i.attribute_5 === integrationType && i.active === "Yes",
  );
};

export default getUserDetails;
